import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { api } from "../api";
import { linkHandler } from "../lib/router";
import type { GrabitForecast, GrabitFutoi, GrabitPoint, GrabitTicker } from "../types";

/**
 * Вкладка Grabit MOEX: прогноз дневного диапазона по ДеМарку и 5-минутные открытые позиции FUTOI физлиц и юрлиц.
 * Перенос static/index.html + app.js + futoi-bars.js исходного приложения: те же данные, правила показа и подписи,
 * в оформлении Insidera. Без входа доступны только демо-инструменты (как в Insidera).
 */

const LAST_KEY = "grabitLastTicker";
const PREFERRED = ["SBER", "IMOEX", "GAZP", "SMLT", "LKOH"];

/** Проценты и обороты — два знака после запятой. */
function fmt(n: number) {
  return Number(n).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Цена — как по расчёту, без округления (сервер отдаёт точный результат формулы); не меньше двух знаков. */
function price(n: number) {
  return Number(n).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 12 });
}

function hasFutoi(t: GrabitTicker) {
  return t.futoi_codes.length > 0;
}

function sortTickers(list: GrabitTicker[]) {
  return [...list].sort((a, b) => {
    // без входа доступные инструменты — сверху
    if (a.locked !== b.locked) return a.locked ? 1 : -1;
    const fa = hasFutoi(a) ? 0 : 1;
    const fb = hasFutoi(b) ? 0 : 1;
    if (fa !== fb) return fa - fb;
    return a.ticker.localeCompare(b.ticker);
  });
}

function readLast(): string | null {
  try {
    return localStorage.getItem(LAST_KEY);
  } catch {
    return null;
  }
}

function writeLast(t: string) {
  try {
    localStorage.setItem(LAST_KEY, t);
  } catch {
    /* хранилище недоступно — просто не запоминаем */
  }
}

export function GrabitPage({ signedIn }: { signedIn: boolean }) {
  const [tickers, setTickers] = useState<GrabitTicker[] | null>(null);
  const [selected, setSelected] = useState<string>("");
  const [forecast, setForecast] = useState<GrabitForecast | null>(null);
  const [status, setStatus] = useState<{ text: string; error: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [tgUrl, setTgUrl] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    api
      .grabitTickers(ctrl.signal)
      .then((list) => {
        setTickers(list);
        const open = list.filter((t) => !t.locked);
        const saved = readLast();
        const pick =
          (saved && open.find((t) => t.ticker === saved)?.ticker) ||
          PREFERRED.find((p) => open.some((t) => t.ticker === p && hasFutoi(t))) ||
          open.find(hasFutoi)?.ticker ||
          open[0]?.ticker ||
          "";
        setSelected(pick);
        if (pick) void calculate(pick);
      })
      .catch((e: unknown) => {
        if (!ctrl.signal.aborted) setStatus({ text: e instanceof Error ? e.message : String(e), error: true });
      });
    api
      .grabitTelegram(ctrl.signal)
      .then((r) => setTgUrl(r.url))
      .catch(() => setTgUrl(null)); // без бота страница остаётся рабочей
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  async function calculate(ticker: string) {
    if (!ticker) return;
    writeLast(ticker);
    setBusy(true);
    setForecast(null);
    setStatus({ text: "Загружаем данные с MOEX…", error: false });
    try {
      const body = await api.grabitForecast(ticker);
      setStatus(null);
      setForecast(body);
    } catch (e) {
      setStatus({ text: e instanceof Error ? e.message : "Не удалось получить прогноз", error: true });
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page grabit-page">
      <div className="grabit-head">
        <div>
          <h1>DeMark · FUTOI</h1>
          <p className="lede">Формулы Тома ДеМарка · Открытые позиции · MOEX</p>
        </div>
        {tgUrl && (
          <a className="btn btn-primary grabit-tg" href={tgUrl} target="_blank" rel="noopener noreferrer">
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M21.9 4.3 2.8 11.7c-1.3.5-1.3 1.2-.2 1.5l4.9 1.5 1.9 5.7c.2.7.1 1 .8.9.4 0 .6-.2.9-.5l2.7-2.6 4.9 3.6c.9.5 1.5.2 1.7-.8l3.2-15c.3-1.3-.5-1.9-1.7-1.4zM9.2 14.6l8.8-5.5c.4-.3.8-.1.5.2l-7.3 6.6-.3 3.2-1.7-4.5z"
              />
            </svg>
            Бот аномалий FUTOI
          </a>
        )}
      </div>

      <section className="panel grabit-picker">
        <TickerPicker
          tickers={tickers}
          selected={selected}
          onSelect={setSelected}
          onSubmit={(t) => void calculate(t)}
          busy={busy}
          signedIn={signedIn}
        />
      </section>

      {status && (
        <p className={status.error ? "notice notice-error" : "notice"} role={status.error ? "alert" : "status"}>
          {status.text}
        </p>
      )}

      {forecast && <ForecastView data={forecast} />}
      {forecast && <FutoiSection key={forecast.ticker} forecast={forecast} />}
    </main>
  );
}

// ------------------------------------------------------------------ выбор инструмента

function TickerPicker({
  tickers,
  selected,
  onSelect,
  onSubmit,
  busy,
  signedIn,
}: {
  tickers: GrabitTicker[] | null;
  selected: string;
  onSelect: (t: string) => void;
  onSubmit: (t: string) => void;
  busy: boolean;
  signedIn: boolean;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const current = tickers?.find((t) => t.ticker === selected);
  const label = current ? `${current.ticker} — ${current.name}` : "";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sortTickers(
      (tickers ?? []).filter((t) => !q || t.ticker.toLowerCase().includes(q) || t.name.toLowerCase().includes(q)),
    );
  }, [tickers, query]);

  useEffect(() => {
    listRef.current?.querySelector(".is-active")?.scrollIntoView({ block: "nearest" });
  }, [hi, open]);

  const pick = (t: GrabitTicker) => {
    if (t.locked) return;
    onSelect(t.ticker);
    setQuery("");
    setOpen(false);
    onSubmit(t.ticker);
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      if (filtered.length) setHi((h) => (h + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (filtered.length) setHi((h) => (h - 1 + filtered.length) % filtered.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && filtered[hi]) pick(filtered[hi]);
      else if (selected) onSubmit(selected);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <>
      <label htmlFor="grabit-search" className="grabit-label">
        Инструмент
      </label>
      <div className="grabit-picker-row">
        <div className="grabit-combo">
          <input
            id="grabit-search"
            className="search grabit-search"
            type="search"
            autoComplete="off"
            spellCheck={false}
            placeholder={label || "Поиск по тикеру или названию"}
            value={open ? query : label}
            role="combobox"
            aria-expanded={open}
            aria-controls="grabit-list"
            aria-autocomplete="list"
            onFocus={() => {
              setQuery("");
              setHi(0);
              setOpen(true);
            }}
            onBlur={() => window.setTimeout(() => setOpen(false), 120)}
            onChange={(e) => {
              setQuery(e.target.value);
              setHi(0);
              setOpen(true);
            }}
            onKeyDown={onKey}
          />
          {open && (
            <ul id="grabit-list" className="grabit-list" role="listbox" ref={listRef}>
              {filtered.length === 0 ? (
                <li className="grabit-empty">Ничего не найдено</li>
              ) : (
                filtered.map((t, i) => (
                  <li
                    key={t.ticker}
                    role="option"
                    aria-selected={t.ticker === selected}
                    aria-disabled={t.locked}
                    className={`grabit-option${hasFutoi(t) ? " has-futoi" : ""}${i === hi ? " is-active" : ""}${t.locked ? " locked" : ""}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pick(t);
                    }}
                  >
                    <span className="grabit-option-main">
                      <span className="grabit-code">{t.ticker}</span>
                      <span className="grabit-name">{t.name}</span>
                    </span>
                    {t.locked ? (
                      <span className="grabit-lock" title="Доступно после входа">
                        после входа
                      </span>
                    ) : hasFutoi(t) ? (
                      <span className="grabit-mark">FUTOI</span>
                    ) : null}
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
        <button type="button" className="btn btn-primary" disabled={busy || !selected} onClick={() => onSubmit(selected)}>
          Рассчитать
        </button>
      </div>
      <p className="muted small grabit-hint">
        Жирным и меткой <span className="grabit-mark">FUTOI</span> — есть открытые позиции.
        {!signedIn && (
          <>
            {" "}
            Без входа открыты {(tickers ?? []).filter((t) => !t.locked).map((t) => t.ticker).join(", ") || "несколько инструментов"};{" "}
            <a href="/login?next=/grabit" onClick={linkHandler("/login?next=/grabit")}>
              войдите
            </a>
            , чтобы видеть все.
          </>
        )}
      </p>
    </>
  );
}

// ------------------------------------------------------------------ прогноз

function ForecastView({ data }: { data: GrabitForecast }) {
  // открытие вне прогноза: пробитая граница переходит на другую сторону, в сторону прорыва цели нет (null)
  const adjusted = data.gap_kind !== "none";
  return (
    <>
      <section className="panel grabit-hero">
        <div className="grabit-hero-top">
          <div>
            <span className={`grabit-badge ${data.market_state}`}>{data.market_state_label}</span>
            <h2>
              {data.ticker} · {data.name}
            </h2>
            <p className="muted">
              Прогноз на {data.forecast_date} · база: {data.base_date}
            </p>
            {data.notes?.map((n) => (
              <p key={n} className="grabit-note">
                {n}
              </p>
            ))}
          </div>
          <div className="grabit-width">
            <span className="grabit-width-label">Ширина диапазона</span>
            <span className="grabit-width-value">{fmt(data.width_pct)}%</span>
          </div>
        </div>
        <div className="grabit-levels">
          <div className="grabit-level high">
            <span className="grabit-level-name">Максимум</span>
            <span className="grabit-level-price">{data.final_high != null ? price(data.final_high) : "—"}</span>
            {data.final_high == null && <span className="grabit-level-hint">Цели сверху нет — прорыв вверх</span>}
            {data.gap_kind === "gap_down" && <span className="grabit-level-hint">Бывший минимум — теперь сопротивление</span>}
            {adjusted && <span className="grabit-level-hint">Базовый прогноз: {price(data.projected_high)}</span>}
          </div>
          <div className="grabit-level low">
            <span className="grabit-level-name">Минимум</span>
            <span className="grabit-level-price">{data.final_low != null ? price(data.final_low) : "—"}</span>
            {data.final_low == null && <span className="grabit-level-hint">Цели снизу нет — прорыв вниз</span>}
            {data.gap_kind === "gap_up" && <span className="grabit-level-hint">Бывший максимум — теперь поддержка</span>}
            {adjusted && <span className="grabit-level-hint">Базовый прогноз: {price(data.projected_low)}</span>}
          </div>
        </div>
        <p className={`grabit-gap ${data.gap_kind}`}>{data.gap_message}</p>
      </section>

      <section className="grabit-grid">
        <article className="panel">
          <h3>Базовый день {data.base_date}</h3>
          <dl className="grabit-ohlc">
            <div><dt>Открытие</dt><dd>{price(data.base_ohlc.open)}</dd></div>
            <div><dt>Максимум</dt><dd>{price(data.base_ohlc.high)}</dd></div>
            <div><dt>Минимум</dt><dd>{price(data.base_ohlc.low)}</dd></div>
            <div><dt>Закрытие</dt><dd>{price(data.base_ohlc.close)}</dd></div>
          </dl>
          <p className="muted">
            Оборот: <strong>{fmt(data.base_turnover_mln)}</strong> млн ₽
          </p>
        </article>
        <article className="panel">
          <h3>Расчёт</h3>
          <dl className="grabit-ohlc">
            <div><dt>X</dt><dd>{price(data.x)}</dd></div>
            <div><dt>Состояние</dt><dd className="text">{data.market_state_label}</dd></div>
            <div><dt>Открытие дня прогноза</dt><dd>{data.today_open != null ? price(data.today_open) : "—"}</dd></div>
            <div><dt>Ширина</dt><dd>{price(data.width)}</dd></div>
          </dl>
        </article>
      </section>
    </>
  );
}

// ------------------------------------------------------------------ FUTOI (futoi-bars.js)

const BAR_BLOCKS = 50;
const MIN_CHANGE = 0.01;
const STRONG_MOVE = 0.1;

function filterByChange(points: GrabitPoint[], key: "long" | "short") {
  if (!points.length) return [];
  const dayMax = Math.max(...points.map((p) => p[key]));
  const keep = new Set<number>([0]);
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1][key];
    const curr = points[i][key];
    const base = Math.abs(prev) || 1;
    if (Math.abs(curr - prev) / base >= MIN_CHANGE) keep.add(i);
  }
  keep.add(points.length - 1);
  // пик дня всегда в списке — иначе подсветка пика пропадает
  points.forEach((p, i) => {
    if (p[key] === dayMax) keep.add(i);
  });
  return [...keep].sort((a, b) => a - b).map((i) => points[i]);
}

function fmtPct(pct: number) {
  const n = Math.abs(pct * 100);
  const rounded = n >= 10 ? Math.round(n) : n.toFixed(1);
  return pct > 0 ? `+${rounded}%` : `−${rounded}%`;
}

function strongMove(value: number, start: number) {
  const base = Math.abs(start) || 1;
  const pct = (value - start) / base;
  if (pct > STRONG_MOVE) return { cls: "up", text: `▲ рост ${fmtPct(pct)}` };
  if (pct < -STRONG_MOVE) return { cls: "down", text: `▼ падение ${fmtPct(pct)}` };
  return null;
}

function barBlocks(value: number, min: number, max: number) {
  const range = max - min || 1;
  return Math.max(1, Math.round(((value - min) / range) * BAR_BLOCKS));
}

function FutoiSection({ forecast }: { forecast: GrabitForecast }) {
  const codes = forecast.futoi_codes;
  const [code, setCode] = useState(codes[0] ?? "");
  const [data, setData] = useState<GrabitFutoi | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;
    const ctrl = new AbortController();
    setData(null);
    setMessage("Загрузка позиций FUTOI…");
    api
      .grabitFutoi(forecast.ticker, code, ctrl.signal)
      .then((d) => {
        setData(d);
        setMessage(null);
      })
      .catch((e: unknown) => {
        if (!ctrl.signal.aborted) setMessage(`Позиции FUTOI (${code}): ${e instanceof Error ? e.message : String(e)}`);
      });
    return () => ctrl.abort();
  }, [forecast.ticker, code]);

  if (!codes.length) {
    return (
      <section className="panel">
        <h3>Открытые позиции FUTOI</h3>
        <p className="muted">
          Для этого инструмента нет FUTOI на срочном рынке. Попробуйте SBER, GAZP, SMLT или IMOEX.
        </p>
      </section>
    );
  }

  return (
    <section className="panel grabit-futoi">
      <h3>Открытые позиции FUTOI</h3>
      {codes.length > 1 && (
        <div className="segmented grabit-codes" role="group" aria-label="Код срочного рынка">
          {codes.map((c) => (
            <button key={c} type="button" className={c === code ? "active" : undefined} aria-pressed={c === code} onClick={() => setCode(c)}>
              {forecast.futoi_labels[c] || c}
            </button>
          ))}
        </div>
      )}
      {data && <FutoiBars data={data} />}
      {message && <p className="muted">{message}</p>}
    </section>
  );
}

function FutoiBars({ data }: { data: GrabitFutoi }) {
  const [group, setGroup] = useState<"fiz" | "yur">("fiz");
  const series = group === "yur" ? data.yur : data.fiz;
  const who = group === "yur" ? "юрлиц" : "физлиц";
  return (
    <>
      <div className="grabit-futoi-head">
        <div>
          <h4>
            {data.futoi_code} · {data.futoi_name}
          </h4>
          <p className="muted small">
            {data.trade_date} · шаг 5 мин · скрыты изменения &lt; 1% · метки ▲▼ при сдвиге &gt; 10% от старта торгов
          </p>
        </div>
        <div className="segmented" role="tablist" aria-label="Тип участников">
          <button type="button" role="tab" aria-selected={group === "fiz"} className={group === "fiz" ? "active" : undefined} onClick={() => setGroup("fiz")}>
            Физлица
          </button>
          <button type="button" role="tab" aria-selected={group === "yur"} className={group === "yur" ? "active" : undefined} onClick={() => setGroup("yur")}>
            Юрлица
          </button>
        </div>
      </div>
      {series.length === 0 ? (
        <p className="muted">Нет точек за сегодня.</p>
      ) : (
        <div className="grabit-panels">
          <BarPanel title={`Лонги ${who}`} accent="long" points={series} field="long" />
          <BarPanel title={`Шорты ${who}`} accent="short" points={series} field="short" />
        </div>
      )}
    </>
  );
}

function BarPanel({
  title,
  accent,
  points,
  field,
}: {
  title: string;
  accent: "long" | "short";
  points: GrabitPoint[];
  field: "long" | "short";
}) {
  const dayMax = Math.max(...points.map((p) => p[field]));
  const start = points[0][field];
  const shown = filterByChange(points, field);
  const min = Math.min(...shown.map((p) => p[field]));
  const max = Math.max(...shown.map((p) => p[field]));
  return (
    <section className={`grabit-bars ${accent}`}>
      <h5>{title}</h5>
      <div className="grabit-rows">
        {shown.map((p) => {
          const v = p[field];
          const move = strongMove(v, start);
          return (
            <div key={p.time} className={`grabit-row${v === dayMax ? " peak" : ""}`}>
              <span className="grabit-row-time">{p.time.slice(0, 5)}</span>
              <span className="grabit-row-bar-wrap">
                <span className={`grabit-row-bar ${accent}`}>{"█".repeat(barBlocks(v, min, max))}</span>
              </span>
              <span className="grabit-row-end">
                <span className="grabit-row-value">{v.toLocaleString("ru-RU")}</span>
                {move && (
                  <span className={`grabit-signal ${move.cls}`} title={move.cls === "up" ? "Сильный рост от старта торгов" : "Сильное падение от старта торгов"}>
                    {move.text}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
